<template>
  <SpecificationSkeleton
    v-if="userSpecMetadata"
    :specMetadata="userSpecMetadata"
    :goBackUrl="`/compliance/user`"
    icon="account_circle"
  >
    <div class="flex flex-col gap-2 px-6 py-4">
      <ExpansionPanel>
        <template #header="{ toggleCollapse }">
          <button
            class="w-full cursor-pointer flex gap-0.5 px-6 py-4"
            @click="toggleCollapse"
          >
            <h3 class="text-lg font-semibold text-dark-neutral-600">
              {{
                `User specifications concerning ${userSpecMetadata.phase.name.toLocaleLowerCase()}`
              }}
            </h3>
          </button>
        </template>
        <div class="px-6 py-4 space-y-2">
          <div v-if="isUsePhase" class="space-y-3">
            <div class="flex gap-0.5">
              <p
                class="block text-sm font-semibold leading-5 text-dark-neutral-500"
              >
                User Categories
              </p>
            </div>
            <div class="flex flex-col gap-2">
              <label class="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  v-model="userSpec.inclusion.userCategories"
                  value="Health Care Professional"
                  type="checkbox"
                  class="w-4 h-4 text-primary-600 border-mid-neutral-100 rounded focus:ring-primary-500"
                />
                <span class="text-dark-neutral-600"
                  >Health Care Professional</span
                >
              </label>
              <div>
                <label class="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    v-model="userSpec.inclusion.userCategories"
                    value="Lay User"
                    type="checkbox"
                    class="w-4 h-4 text-primary-600 border-mid-neutral-100 rounded focus:ring-primary-500"
                  />
                  <span class="text-dark-neutral-600">Lay User</span>
                </label>
                <div
                  v-if="userSpec.inclusion.userCategories.includes('Lay User')"
                  class="pl-8 mt-1"
                >
                  <label class="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      v-model="userSpec.inclusion.userCategories"
                      value="Patient"
                      type="checkbox"
                      class="w-4 h-4 text-primary-600 border-mid-neutral-100 rounded focus:ring-primary-500"
                    />
                    <span class="text-dark-neutral-600">Patient</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div
              class="flex text-sm gap-2 font-medium items-center"
              role="tablist"
            >
              <div class="flex items-center overflow-auto relative">
                <button
                  v-if="canScrollLeft"
                  @click="scrollTabs('left')"
                  class="p-2 rounded hover:bg-mid-neutral-50 bg-mid-neutral-50 absolute top-0 left-0 flex items-center h-full z-1"
                  title="Scroll left"
                  type="button"
                >
                  <span class="material-symbols-outlined text-base"
                    >chevron_left</span
                  >
                </button>

                <div
                  ref="tabBarRef"
                  class="flex gap-2 flex-1 overflow-x-auto scrollbar-hide"
                  style="scrollbar-width: none; -ms-overflow-style: none"
                >
                  <button
                    v-for="(profile, idx) in userSpec.inclusion.profiles"
                    :key="'profile-tab-' + idx"
                    role="tab"
                    :aria-selected="idx === currentProfileIndex"
                    class="relative p-4 transition-colors flex items-center group whitespace-nowrap"
                    :class="[
                      idx === currentProfileIndex
                        ? 'text-primary-500 border-b-2 border-primary-500 bg-white'
                        : 'text-mid-neutral-500 hover:text-primary-500 border-b-2 border-transparent hover:border-primary-500',
                    ]"
                    @click="goToProfile(idx)"
                  >
                    Profile {{ idx + 1 }}
                  </button>
                </div>

                <button
                  v-if="canScrollRight"
                  @click="scrollTabs('right')"
                  class="p-2 ml-1 rounded hover:bg-mid-neutral-50 bg-mid-neutral-50 absolute top-0 right-0 flex items-center h-full z-1"
                  title="Scroll right"
                  type="button"
                >
                  <span class="material-symbols-outlined text-base"
                    >chevron_right</span
                  >
                </button>
              </div>

              <button
                class="ml-2 px-2 py-1 rounded text-primary-600 hover:text-primary-300 whitespace-nowrap"
                @click="addProfile"
              >
                + Add Profile
              </button>
            </div>
            <Transition v-bind="transitionClasses" mode="out-in">
              <div
                v-if="currentProfile"
                :key="currentProfileIndex"
                class="space-y-6 bg-white rounded-b border border-mid-neutral-50 px-6 py-4"
              >
                <div class="space-y-3">
                  <div class="flex gap-0.5">
                    <p
                      class="block text-sm font-semibold leading-5 text-dark-neutral-500"
                    >
                      Age Range
                    </p>
                  </div>
                  <div class="flex items-center gap-4">
                    <div class="flex items-center gap-2">
                      <span class="text-sm text-dark-neutral-400">From</span>
                      <NumberInput
                        v-model="currentProfile.age.min"
                        :min="0"
                        :max="120"
                        inputClasses="w-24"
                      />
                    </div>
                    <div class="flex items-center gap-2">
                      <span class="text-sm text-dark-neutral-400">To</span>
                      <NumberInput
                        v-model="currentProfile.age.max"
                        :min="0"
                        :max="120"
                        inputClasses="w-24"
                      />
                      <span class="text-xs text-dark-neutral-400">years</span>
                    </div>
                  </div>
                  <p v-if="ageRangeError" class="text-sm text-red-600">
                    {{ ageRangeError }}
                  </p>
                </div>

                <div class="space-y-3">
                  <SelectInput
                    :items="genderOptions"
                    v-model="currentProfile.gender"
                    label="Gender"
                    placeholder="All genders"
                    selectClasses="w-60"
                    class="w-100"
                  />
                </div>

                <div class="space-y-3">
                  <div class="flex gap-0.5">
                    <p
                      class="block text-sm font-semibold leading-5 text-dark-neutral-500"
                    >
                      Language Proficiency (if required)
                    </p>
                  </div>
                  <div class="flex flex-wrap gap-3">
                    <label
                      v-for="language in availableLanguages"
                      :key="language"
                      class="flex items-center gap-2 text-sm cursor-pointer"
                    >
                      <input
                        v-model="currentProfile.linguistic"
                        :value="language"
                        type="checkbox"
                        class="w-4 h-4 text-primary-600 border-mid-neutral-100 rounded focus:ring-primary-500"
                      />
                      <span class="text-dark-neutral-600">{{ language }}</span>
                    </label>
                  </div>
                </div>

                <div class="space-y-3">
                  <div class="flex gap-0.5">
                    <p
                      class="block text-sm font-semibold leading-5 text-dark-neutral-500"
                    >
                      Cultural Considerations
                    </p>
                  </div>
                  <div class="flex flex-wrap gap-4 items-center">
                    <div
                      v-for="(item, index) in currentProfile.culturalBackground"
                      :key="'cultural-' + item + index"
                      class="flex items-center gap-1 px-4 py-2.5 text-sm font-medium rounded-full text-primary-600 bg-primary-50"
                    >
                      <span class="truncate max-w-[252px]">{{ item }}</span>
                      <button
                        @click="
                          removeInclusionItem('culturalBackground', index)
                        "
                      >
                        <XMarkIcon class="w-4 h-4" />
                      </button>
                    </div>
                    <FloatingTextInput
                      @save="addInclusionItem('culturalBackground', $event)"
                      placeholder="Type cultural background"
                    >
                      <template #activator="{ show }">
                        <button
                          class="flex items-center gap-1 text-sm border-2 p-2 pr-3 rounded-full text-slate-500 border-slate-300 border-dashed font-semibold"
                          @click.stop="show"
                        >
                          <PlusIcon class="w-4 h-4" /> Add cultural background
                        </button>
                      </template>
                    </FloatingTextInput>
                  </div>
                </div>

                <div class="space-y-3">
                  <div class="flex gap-0.5">
                    <p
                      class="block text-sm font-semibold leading-5 text-dark-neutral-500"
                    >
                      Education / Professional Competence
                    </p>
                  </div>
                  <div class="flex flex-wrap gap-4 items-center">
                    <div
                      v-for="(item, index) in currentProfile.educationLevel"
                      :key="'education-' + item + index"
                      class="flex items-center gap-1 px-4 py-2.5 text-sm font-medium rounded-full text-primary-600 bg-primary-50"
                    >
                      <span class="truncate max-w-[252px]">{{ item }}</span>
                      <button
                        @click="removeInclusionItem('educationLevel', index)"
                      >
                        <XMarkIcon class="w-4 h-4" />
                      </button>
                    </div>
                    <FloatingTextInput
                      @save="addInclusionItem('educationLevel', $event)"
                      placeholder="Type education level"
                    >
                      <template #activator="{ show }">
                        <button
                          class="flex items-center gap-1 text-sm border-2 p-2 pr-3 rounded-full text-slate-500 border-slate-300 border-dashed font-semibold"
                          @click.stop="show"
                        >
                          <PlusIcon class="w-4 h-4" /> Add education level
                        </button>
                      </template>
                    </FloatingTextInput>
                  </div>
                </div>

                <div class="space-y-3">
                  <div class="flex gap-0.5">
                    <p
                      class="block text-sm font-semibold leading-5 text-dark-neutral-500"
                    >
                      Specific Qualifications / Certifications
                    </p>
                  </div>
                  <div class="flex flex-wrap gap-4 items-center">
                    <div
                      v-for="(item, index) in currentProfile.qualifications"
                      :key="'qualification-' + item + index"
                      class="flex items-center gap-1 px-4 py-2.5 text-sm font-medium rounded-full text-primary-600 bg-primary-50"
                    >
                      <span class="truncate max-w-[252px]">{{ item }}</span>
                      <button
                        @click="removeInclusionItem('qualifications', index)"
                      >
                        <XMarkIcon class="w-4 h-4" />
                      </button>
                    </div>
                    <FloatingTextInput
                      @save="addInclusionItem('qualifications', $event)"
                      placeholder="Type qualification"
                    >
                      <template #activator="{ show }">
                        <button
                          class="flex items-center gap-1 text-sm border-2 p-2 pr-3 rounded-full text-slate-500 border-slate-300 border-dashed font-semibold"
                          @click.stop="show"
                        >
                          <PlusIcon class="w-4 h-4" /> Add qualification
                        </button>
                      </template>
                    </FloatingTextInput>
                  </div>
                </div>

                <div class="space-y-3">
                  <div class="flex gap-0.5">
                    <p
                      class="block text-sm font-semibold leading-5 text-dark-neutral-500"
                    >
                      Device-Specific Training Required
                    </p>
                  </div>
                  <Switch v-model="currentProfile.specificTraining">
                    <span class="text-sm text-dark-neutral-600">
                      {{
                        currentProfile.specificTraining
                          ? "Required"
                          : "Not required"
                      }}
                    </span>
                  </Switch>
                </div>

                <div>
                  <button
                    v-if="userSpec.inclusion.profiles.length > 1"
                    class="flex items-center gap-1 px-2 py-2 rounded text-red-600 hover:bg-red-50 transition-colors"
                    @click="removeProfile(currentProfileIndex)"
                    title="Delete profile"
                  >
                    <span class="material-symbols-outlined text-xl"
                      >delete</span
                    >
                    Delete Profile
                  </button>
                </div>
              </div>
            </Transition>
          </div>
        </div>
      </ExpansionPanel>

      <ExpansionPanel>
        <template #header="{ toggleCollapse }">
          <button
            class="w-full cursor-pointer flex gap-0.5 px-6 py-4"
            @click="toggleCollapse"
          >
            <h3 class="text-lg font-semibold text-dark-neutral-600">
              {{
                `User risks concerning ${userSpecMetadata.phase.name.toLocaleLowerCase()}`
              }}
            </h3>
          </button>
        </template>
        <div class="space-y-6 px-6 py-4 border-b border-mid-neutral-50">
          <div class="space-y-3">
            <div class="flex gap-0.5">
              <p
                class="block text-sm font-semibold leading-5 text-dark-neutral-500"
              >
                Excluded Factors
              </p>
            </div>
            <div class="flex flex-wrap gap-4 items-center">
              <div
                v-for="(item, index) in userSpec.exclusion.factors"
                :key="'exclusion-' + item + index"
                class="flex items-center gap-1 px-4 py-2.5 text-sm font-medium rounded-full text-primary-600 bg-primary-50"
              >
                <span class="truncate max-w-[252px]">{{ item }}</span>
                <button @click="removeExclusionItem(index)">
                  <XMarkIcon class="w-4 h-4" />
                </button>
              </div>
              <FloatingTextInput
                @save="addExclusionItem($event)"
                placeholder="Type exclusion factor"
              >
                <template #activator="{ show }">
                  <button
                    class="flex items-center gap-1 text-sm border-2 p-2 pr-3 rounded-full text-slate-500 border-slate-300 border-dashed font-semibold"
                    @click.stop="show"
                  >
                    <PlusIcon class="w-4 h-4" /> Add exclusion factor
                  </button>
                </template>
              </FloatingTextInput>
            </div>
          </div>
        </div>
      </ExpansionPanel>
    </div>
  </SpecificationSkeleton>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from "vue";
import {
  useUserSpecificationState,
  useUsersSpecificationMetadataState,
} from "states/user";
import {
  Switch,
  FloatingTextInput,
  NumberInput,
  SelectInput,
  ExpansionPanel,
} from "ui-components";

import { PlusIcon, XMarkIcon } from "@heroicons/vue/24/outline";
import SpecificationSkeleton from "components/SpecificationSkeleton.vue";
import { lifecyclePhasesState, PhaseId } from "states/lifecyclePhases";
import { variantsState } from "states/variants";
import { useRoute } from "vue-router";
import { generateSpecificationMetadataFromState } from "utils/generateSpecificationMetadataFromState";

const usersSpecs = useUsersSpecificationMetadataState();
const variants = variantsState();
const { phases } = lifecyclePhasesState();
const route = useRoute();

const userSpecMetadata = computed(() => {
  const specState = usersSpecs.value.find(
    (spec) => spec.id === route.params.id
  );
  if (!specState) return null;

  return generateSpecificationMetadataFromState({
    state: specState,
    variants: variants.value,
    phases: phases.value,
  });
});

const userSpec = useUserSpecificationState(route.params.id as string);

const isUsePhase = computed(
  () => userSpecMetadata.value?.phase.id === PhaseId.USE
);

const genderOptions = ["Male", "Female"];

const availableLanguages = [
  "English",
  "Spanish",
  "French",
  "German",
  "Italian",
  "Portuguese",
  "Dutch",
  "Chinese",
  "Japanese",
  "Korean",
  "Arabic",
  "Hindi",
];

const ageRangeError = ref("");

function addExclusionItem(value: string) {
  const trimmed = value.trim();
  if (trimmed && !userSpec.value.exclusion.factors.includes(trimmed)) {
    userSpec.value.exclusion.factors.push(trimmed);
  }
}
function removeExclusionItem(index: number) {
  userSpec.value.exclusion.factors.splice(index, 1);
}
function addInclusionItem(
  key: keyof typeof currentProfile.value,
  value: string
) {
  const trimmed = value.trim();
  if (!trimmed) return;
  const arr = currentProfile.value[key] as unknown;
  if (Array.isArray(arr) && !arr.includes(trimmed)) arr.push(trimmed);
}
function removeInclusionItem(
  key: keyof typeof currentProfile.value,
  index: number
) {
  const arr = currentProfile.value[key] as unknown;
  if (Array.isArray(arr)) arr.splice(index, 1);
}

const currentProfileIndex = ref(0);
const currentProfile = computed(
  () => userSpec.value.inclusion.profiles[currentProfileIndex.value]
);

const isForward = ref(true);
function goToProfile(idx: number) {
  isForward.value = idx > currentProfileIndex.value;
  currentProfileIndex.value = idx;
}

const transitionClasses = computed<Record<string, string>>(() => {
  const enterFrom = isForward.value
    ? "opacity-0 translate-x-10"
    : "opacity-0 -translate-x-10";
  const leaveTo = isForward.value
    ? "opacity-0 -translate-x-10"
    : "opacity-0 translate-x-10";
  const active = "transition-all duration-50 ease-[cubic-bezier(0.4,0,0.2,1)]";

  return {
    "enter-active-class": active,
    "leave-active-class": active,
    "enter-from-class": enterFrom,
    "enter-to-class": "opacity-100 translate-x-0",
    "leave-from-class": "opacity-100 translate-x-0",
    "leave-to-class": leaveTo,
  };
});

function addProfile() {
  const newProfile = {
    age: { min: 0, max: 120 },
    gender: "",
    linguistic: [],
    culturalBackground: [],
    educationLevel: [],
    qualifications: [],
    specificTraining: false,
  };
  userSpec.value.inclusion.profiles.push(newProfile);
  isForward.value = true;
  currentProfileIndex.value = userSpec.value.inclusion.profiles.length - 1;
}

function removeProfile(index: number) {
  if (userSpec.value.inclusion.profiles.length <= 1) return;
  isForward.value = !(index < currentProfileIndex.value);
  userSpec.value.inclusion.profiles.splice(index, 1);
  if (currentProfileIndex.value >= userSpec.value.inclusion.profiles.length) {
    currentProfileIndex.value = userSpec.value.inclusion.profiles.length - 1;
  }
}

// ----- Scrollable tab bar helpers -----
const tabBarRef = ref<HTMLDivElement | null>(null);
const canScrollLeft = ref(false);
const canScrollRight = ref(false);

function updateScrollButtons() {
  const el = tabBarRef.value;
  if (!el) return;
  canScrollLeft.value = el.scrollLeft > 0;
  canScrollRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
}

function scrollTabs(direction: "left" | "right") {
  const el = tabBarRef.value;
  if (!el) return;
  const scrollAmount = 300;
  el.scrollBy({
    left: direction === "left" ? -scrollAmount : scrollAmount,
    behavior: "smooth",
  });
  setTimeout(updateScrollButtons, 350);
}

onMounted(() => {
  const el = tabBarRef.value;
  if (!el) return;
  el.addEventListener("scroll", updateScrollButtons);
  updateScrollButtons();
});

watch(
  () => userSpec.value.inclusion.profiles.length,
  () => setTimeout(updateScrollButtons, 100)
);
</script>

<!-- No <style> block needed: transition is Tailwind-only -->
